import React from "react";
import { LegacyCard, InlineGrid, TextField, Select, Checkbox, } from "@shopify/polaris";
import CustomAutocomplete from "../CustomAutocomplete";
import {
  ButtonNumber,
  SatinNumbers,
  NameLabelColor,
  } from "../CoatData";
import { useLibrary } from "../LibraryContext";


const MenPantForm = ({
  form,
  setForm,
  errors,
  conditionalFieldsMap,
  baseUrl,
  setApploader,
  viewOrder
}) => {
  const { fabrics: Fabrics, linings: LiningNumbers } = useLibrary();
  const ValueAllSelect = [...LiningNumbers, ...SatinNumbers, ...Fabrics];
  const handleChange =
    (section, key = null) =>
      (values) => {

        const trimmedKey = key?.trim();
        let value;
        if (Array.isArray(values)) {
          value = values[0];
        } else {
          value = values;
        }
        const sectionMap = conditionalFieldsMap[section];
        const fieldRule = sectionMap?.[trimmedKey];

        if (fieldRule) {
          const { conditionValue, dependentKey } = fieldRule;
          if (value === conditionValue) {
            // Add the dependent field
            setForm((prev) => ({
              ...prev,
              [section]: {
                ...prev[section],
                [trimmedKey]: value,
                [dependentKey]: "",
              },
            }));
          } else {
            // Remove the dependent field
            const updatedSection = { ...form[section] };
            delete updatedSection[dependentKey];
            updatedSection[trimmedKey] = value;

            setForm((prev) => ({
              ...prev,
              [section]: updatedSection,
            }));
          }

          return;
        }
        // Default update
        if (trimmedKey) {
          setForm((prev) => ({
            ...prev,
            [section]: {
              ...prev[section],
              [trimmedKey]: value,
            },
          }));
        } else {
          setForm((prev) => ({
            ...prev,
            [section]: value,
          }));
        }
      };

  const pantFields = [
    {
      label: "Front Style",
      key: "frontStyle",
      type: "select",
      options: ["No Pleat", "Single Pleat", "Double Pleat"],
    },
    {
      label: "Front Pocket",
      key: "frontPocket",
      type: "select",
      options: ["Slant", "Sideseam", "Besom", "Jean"],
    },
    {
      label: "Back Pocket",
      key: "backPocket",
      type: "select",
      options: [
        "Besom with Button",
        "Besom",
        "Jean Patch with Point",
        "No Back Pocket",
      ],
    },
    {
      label: "Bottom Style",
      key: "bottomStyle",
      type: "select",
      options: ["Regular", 'Cuff 1 1/2"', 'Cuff 2"', 'Cuff 3"'],
    },
    {
      label: "Waistband Closure",
      key: "waistbandClosure",
      type: "select",
      options: [
        "Hook & Button",
        "Hook & Hook",
        "Button & Button",
        "No Extention with Button",
        "No Extention with Hook ",
      ],
    },
    {
      label: "Belt Loop",
      key: "beltLoop",
      type: "select",
      options: ["Regular", "No"],
    },
    {
      label: "Side Tabs",
      key: "sideTabs",
      type: "select",
      options: ["No", "Yes"],
    },
    {
      label: "Waist Width",
      key: "waistWidth",
      type: "select",
      options: ['1 1/2"', '2"'],
    },
    {
      label: "Inside Waistband",
      key: "insideWaistband",
      type: "select",
      options: ["Snugtex", "Fabric"],
    },
    {
      label: "Suspender Buttons",
      key: "suspenderButtons",
      type: "select",
      options: ["No", "Yes"],
    },
    {
      label: "Contrast Side Seam Thin Piping",
      key: "contrastSideSeamThinPiping",
      type: "select",
      options: ["No", "Yes"],
    },
    {
      label: "Contrast Side Seam Wide",
      key: "contrastSideSeamWide",
      type: "select",
      options: ["No", "Yes"],
    },
    {
      label: "Contrast Waistband",
      key: "contrastWaistband",
      type: "select",
      options: ["No", "Yes"],
    },
    {
      label: "Covered Button",
      key: "contrastButtonCovered",
      type: "select",
      options: ["No", "Match Fabric", "Yes"],
    },
    {
      label: "Button Number",
      key: "buttonNumber",
      type: "select",
      options: ButtonNumber,
    },
    {
      label: "Name Label Font",
      key: "nameLabelFont",
      type: "select",
      options: [
        "Caligraphy",
        "Times Roman",
        "Block",
        "Script",
        "Bradley",
        "Old English"
      ],
    },
    {
      label: "Name Label Color",
      key: "nameLabelColor",
      type: "select",
      options: NameLabelColor,
    },
    { label: "Name Label Text", key: "nameLabelText" },
  ];

  const monogramOptions = ["Heel Gaurd"];

  // 3. Add a change handler for monogram checkboxes
  const handleMonogramChange = (label, checked) => {
    setForm((prev) => {
      const updated = checked
        ? [...prev.menpant.monograms, label]
        : prev.menpant.monograms.filter((m) => m !== label);

      const updatedDetails = { ...prev.menpant.monogramDetails };
      if (checked && !updatedDetails[label]) {
        updatedDetails[label] = {
          font: "Calligraphy",
          color: "102 White",
        };
      }
      if (!checked) delete updatedDetails[label];

      return {
        ...prev,
        menpant: {
          ...prev.menpant,
          monograms: updated,
          monogramDetails: updatedDetails,
        },
      };
    });
  };

  const handleMonogramFieldChange = (label, key, value) => {
    setForm((prev) => ({
      ...prev,
      menpant: {
        ...prev.menpant,
        monogramDetails: {
          ...prev.menpant.monogramDetails,
          [label]: {
            ...prev.menpant.monogramDetails[label],
            [key]: value,
          },
        },
      },
    }));
  };


  const renderSection = (title, fields, sectionKey) => (
    <LegacyCard title={title} sectioned>
      <InlineGrid columns={{ xs: 1, sm: 1, md: 2, lg: 3 }} gap="300">
        {fields.map(({ label, key, type = "text", options = [] }) => {
          if (type === "select") {
            return (
              <React.Fragment key={key}>
                {key !== "buttonNumber" && (
                  <div className="two_culumn">
                    <Select
                      label={label}
                      options={options}
                      value={form[sectionKey]?.[key] || ""}
                      onChange={handleChange(sectionKey, key)}
                      error={errors[sectionKey]?.[key]} />
                    {key === "contrastSideSeamThinPiping" &&
                      form[sectionKey]?.[key] === "Yes" && (
                        <CustomAutocomplete
                          label="Lining Number "
                          optionsList={LiningNumbers}
                          onChange={handleChange(sectionKey, "contrastSideSeamThinPipingLining")}
                          defaultValue={form[sectionKey]?.["contrastSideSeamThinPipingLining"] || ""} />
                      )}
                    {key === "contrastSideSeamWide" &&
                      form[sectionKey]?.[key] === "Yes" && (
                        <CustomAutocomplete
                          label="Fabric / Satin / Lining "
                          optionsList={ValueAllSelect}
                          onChange={handleChange(sectionKey, "contrastSideSeamWideSatin")}
                          defaultValue={form[sectionKey]?.["contrastSideSeamWideSatin"] || ""} />
                      )}
                    {key === "contrastWaistband" &&
                      form[sectionKey]?.[key] === "Yes" && (
                        <CustomAutocomplete
                          label="Fabric / Satin / Lining"
                          optionsList={ValueAllSelect}
                          onChange={handleChange(sectionKey, "contrastWaistbandSatin")}
                          defaultValue={form[sectionKey]?.["contrastWaistbandSatin"] || ""} />
                      )}
                    {key === "contrastButtonCovered" &&
                      form[sectionKey]?.[key] === "Yes" && (
                        <CustomAutocomplete
                          label="Fabric / Satin / Lining"
                          optionsList={ValueAllSelect}
                          onChange={handleChange(sectionKey, "contrastButtonCoveredSatin")}
                          defaultValue={
                            form[sectionKey]?.["contrastButtonCoveredSatin"] || ""
                          }
                        />
                      )}
                  </div>
                )}
                {key === "buttonNumber" &&
                  !(form[sectionKey]?.contrastButtonCovered === "Yes" || form[sectionKey]?.contrastButtonCovered === "Match Fabric") && (
                    <CustomAutocomplete
                      label={label}
                      optionsList={options}
                      onChange={handleChange(sectionKey, key)}
                      defaultValue={form[sectionKey]?.[key] || ""} />
                  )}
              </React.Fragment>
            );
          }
          return (
            <TextField
              key={key}
              label={label}
              value={form[sectionKey]?.[key] || ""}
              onChange={handleChange(sectionKey, key)}
              error={errors[sectionKey]?.[key]} />
          );
        })}
      </InlineGrid>
      <div className="multiple_option_checkbox">
        <h3>Add Monogram</h3>
        <InlineGrid columns={{ xs: 2, sm: 2, md: 3, lg: 6 }} gap="100">
          {monogramOptions.map((label) => (
            <Checkbox
              key={label}
              label={label}
              checked={form.menpant.monograms.includes(label)}
              onChange={(val) => handleMonogramChange(label, val)} />
          ))}
        </InlineGrid>
      </div>
      {form.menpant.monograms.map((label, index) => (
        <div
          key={`monogram-${label}-${index}`}
          className="monogram_option_list"
        >
          <h3>{`Monogram Details - ${label}`}</h3>
          <InlineGrid columns={{ xs: 1, sm: 1, md: 2, lg: 3 }} gap="400">
            <div>
              <Select
                label="Font"
                options={[
                  "Caligraphy",
                  "Times Roman",
                  "Block",
                  "Script",
                  "Bradley",
                  "Old English",
                ]}
                value={form.menpant.monogramDetails[label]?.font || ""}
                onChange={(val) => handleMonogramFieldChange(label, "font", val)} />
            </div>
            <Select
              label="Color"
              options={NameLabelColor}
              value={form.menpant.monogramDetails[label]?.color || ""}
              onChange={(val) => handleMonogramFieldChange(label, "color", val)} />
            <TextField
              label="Heel Gaurd Text"
              value={form.menpant.monogramDetails[label]?.text}
              onChange={(val) => handleMonogramFieldChange(label, "text", val)} />
          </InlineGrid>
        </div>
      ))}
    </LegacyCard>
  );

  return renderSection("Men Pant", pantFields, "menpant");
};

export default MenPantForm;
